// src/hooks/usePayloadApi.tsx

import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  ReactNode,
} from 'react'

import { PayloadPool } from '@/types/domain/payload'

import { authHeader, notifyError } from '../api/'
import { usePreloader } from '../components/'

import { refreshToken } from './auth/'
import { OperatorData, useOperator } from './useOperatorApi'

const { REACT_APP_API_URL } = import.meta.env

const PayloadCtx = createContext({ data: [] })

type Props = {
  children: ReactNode
}

export const PayloadDataProvider = ({ children }: Props) => {
  const opData: OperatorData = useOperator()
  const isAdmin = opData?.isAdmin
  const [data, setData] = useState([])
  const { getPool } = usePayloadApi()

  useEffect(() => {
    if (!isAdmin) {
      setData([])
      return
    }
    ;(async () => {
      try {
        const result = await getPool()
        setData(result)
      } catch (error) {
        notifyError(`payloads pool error ${error?.message}`)
      }
    })()
  }, [getPool, isAdmin])

  return <PayloadCtx.Provider value={{ data }}>{children}</PayloadCtx.Provider>
}

export const usePayloadPool = (): PayloadPool => useContext(PayloadCtx).data

export const usePayloadApi = () => {
  const { start, stop } = usePreloader()

  const pool = useContext(PayloadCtx)

  const opData: OperatorData = useOperator()

  const isAdmin = !!opData?.isAdmin

  const withRequest = useCallback(async fn => {
    try {
      await refreshToken()
      return await fn()
    } catch (error) {
      notifyError(`payloads request error ${error?.message}`)
      return null
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getPool = useCallback(() => {
    if (!isAdmin) return []
    if (pool && Array.isArray(pool.data) && pool.data.length > 0)
      return pool.data

    return withPreloader(() =>
      withRequest(async () => {
        await refreshToken()
        const url = `${REACT_APP_API_URL}/system/payload`
        const options = { headers: { ...authHeader() }, method: 'GET' }
        const response = await fetch(url, options)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const payload = await response.json()
        return payload?.data ?? []
      })
    )
  }, [withRequest, pool, isAdmin, withPreloader])

  return {
    getPool,
  }
}
